import * as React from 'react';
import { ReactElement, isValidElement, SVGProps, useLayoutEffect, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import {
  AxisDomainTypeInput,
  AxisInterval,
  AxisTick,
  RenderableAxisProps,
  PresentationAttributesAdaptChildEvent,
  Size,
  AxisDomain,
  ScaleType,
  EvaluatedAxisDomainType,
  TickProp,
  YAxisTickContentProps,
  TickItem,
} from '../util/types';
import { CartesianAxis, CartesianAxisRef, defaultCartesianAxisProps } from './CartesianAxis';
import {
  addYAxis,
  replaceYAxis,
  removeYAxis,
  updateYAxisWidth,
  YAxisOrientation,
  YAxisPadding,
  YAxisSettings,
  YAxisWidth,
  NiceTicksAlgorithm,
} from '../state/cartesianAxisSlice';
import { useAppDispatch, useAppSelector } from '../state/hooks';
import {
  implicitYAxis,
  selectTicksOfAxis,
  selectYAxisPosition,
  selectYAxisSettingsNoDefaults,
  selectYAxisSize,
} from '../state/selectors/axisSelectors';
import { selectAxisViewBox } from '../state/selectors/selectChartOffsetInternal';
import { useIsPanorama } from '../context/PanoramaContext';
import { isLabelContentAFunction } from '../component/Label';
import { RequiresDefaultProps, resolveDefaultProps } from '../util/resolveDefaultProps';
import { axisPropsAreEqual } from '../util/axisPropsAreEqual';
import { CustomScaleDefinition } from '../util/scale/CustomScaleDefinition';
import { useCartesianChartLayout } from '../context/chartLayoutContext';
import { getAxisTypeBasedOnLayout } from '../util/getAxisTypeBasedOnLayout';

interface YAxisProps<DataPointType = any, DataValueType = any> extends Omit<
  RenderableAxisProps<DataPointType, DataValueType>,
  'axisLine' | 'domain' | 'scale' | 'tick'
> {
  /**
   * Determines how the axis line is drawn. Options:
   * - `true`: the axis line is drawn with default props;
   * - `false`: the axis line is not visible;
   * - `object`: passed as props to SVG `<line>` element representing the axis line.
   *
   * @example <YAxis axisLine={false} />
   * @example <YAxis axisLine={{ stroke: 'red', strokeWidth: 2 }} />
   * @defaultValue true
   */
  axisLine?: boolean | SVGProps<SVGLineElement>;
  /**
   * The type of axis.
   *
   * `category`: Treats data as distinct values.
   * Each value is in the same distance from its neighbors, regardless of their actual numeric difference.
   *
   * `number`: Treats data as continuous range.
   * Values that are numerically closer are placed closer together on the axis.
   *
   * `auto`: the type is inferred based on the chart layout.
   *
   * @defaultValue number
   */
  type?: AxisDomainTypeInput;
  /**
   * Specify the domain of axis when the axis is a number axis.
   *
   * If undefined, then the domain is calculated based on the data and dataKeys.
   *
   * The length of domain should be 2, and we will validate the values in domain.
   *
   * Each element in the array can be a number, 'auto', 'dataMin', 'dataMax', a string like 'dataMin - 20', 'dataMax + 100',
   * or a function that accepts a single argument and returns a number.
   *
   * If any element of domain is set to be 'auto', comprehensible scale ticks will be calculated, and the final domain of axis is generated by the ticks.
   * If a function, receives '[dataMin, dataMax]', and must return a computed domain as '[min, max]'.
   *
   * @example <YAxis type="number" domain={['dataMin', 'dataMax']} />
   * @example <YAxis type="number" domain={[0, 'dataMax']} />
   * @example <YAxis type="number" domain={['auto', 'auto']} />
   * @example <YAxis type="number" domain={[0, 'dataMax + 1000']} />
   * @example <YAxis type="number" domain={['dataMin - 100', 'dataMax + 100']} />
   * @example <YAxis type="number" domain={[dataMin => (0 - Math.abs(dataMin)), dataMax => (dataMax * 2)]} />
   * @example <YAxis type="number" domain={([dataMin, dataMax]) => { const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax)); return [-absMax, absMax]; }} />
   * @example <YAxis type="number" domain={[0, 100]} allowDataOverflow />
   */
  domain?: AxisDomain;
  /**
   * Scale function determines how data values are mapped to visual values.
   * In other words, decided the mapping between data domain and coordinate range.
   *
   * If undefined, or 'auto', the scale function is created internally according to the type of axis and data.
   *
   * You can define a custom scale, either as a string shortcut to a d3 scale, or as a complete scale definition object.
   *
   * @defaultValue auto
   * @example <YAxis scale="log" />
   * @example
   * import { scaleLog } from 'd3-scale';
   * const scale = scaleLog().base(Math.E);
   * <YAxis scale={scale} />
   */
  scale?:
    | ScaleType
    | CustomScaleDefinition
    | CustomScaleDefinition<string>
    | CustomScaleDefinition<number>
    | CustomScaleDefinition<Date>;
  /**
   * Unique ID that represents this YAxis.
   * Required when there are multiple YAxes.
   *
   * @defaultValue 0
   */
  yAxisId?: string | number;
  /**
   * Defines how the individual label text is rendered.
   * This controls the settings for individual ticks; on a typical axis, there are multiple ticks, depending on your data.
   *
   * If you want to customize the overall axis label, use the `label` prop instead.
   *
   * Options:
   * - `false`: Do not render any tick labels.
   * - `true`: Render tick labels with default settings.
   * - `object`: An object of props to be merged into the internally calculated tick props.
   * - `ReactElement`: A custom SVG React element to be used as the tick label.
   *   HTML elements such as `<div>` are not valid inside the chart SVG and may trigger React DOM warnings.
   * - `function`: A function that returns a React element for custom rendering of tick labels.
   *
   * @defaultValue true
   */
  tick?: TickProp<YAxisTickContentProps>;
  /**
   * Ticks can be any type when the axis is the type of category
   * Ticks must be numbers when the axis is the type of number
   *
   * @see {@link https://recharts.github.io/guide/axisTicks/}
   */
  ticks?: ReadonlyArray<AxisTick>;
  /**
   * Width of the axis in pixels.
   * `auto` will attempt to resize the axis based on its content.
   *
   * @defaultValue 60
   */
  width?: YAxisWidth;
  /**
   * If set true, flips ticks around the axis line, displaying the labels inside the chart instead of outside.
   * @defaultValue false
   */
  mirror?: boolean;
  /**
   * The orientation of axis.
   * @defaultValue left
   */
  orientation?: YAxisOrientation;
  /**
   * Axis padding is the distance between the edge of plot area and the first/last tick.
   *
   * @defaultValue {"top":0,"bottom":0}
   */
  padding?: YAxisPadding;
  /**
   * The minimum gap between two adjacent tick labels
   *
   * @defaultValue 5
   */
  minTickGap?: number;
  /**
   * If set 0, all the ticks will be shown. If set "preserveStart", "preserveEnd" or "preserveStartEnd",
   * the ticks which is to be shown or hidden will be calculated automatically.
   *
   * @defaultValue preserveEnd
   */
  interval?: AxisInterval;
  /**
   * The margin between tick line and tick.
   */
  tickMargin?: number;
  /**
   * Specifies the font size of tick text.
   * If this prop is not specified, Recharts will read the default tick size from the DOM.
   *
   * Font size is used to calculate the width of the ticks, which in turn is used to determine how many ticks are rendered without overlapping.
   */
  fontSize?: number | string;
  /**
   * Specifies the letter spacing of ticks.
   * This is used in conjunction with fontSize to calculate the width of the ticks.
   * If this prop is not specified, Recharts will read the default letter spacing from the DOM.
   *
   * Letter spacing is used to calculate the width of the ticks, which in turn is used to determine how many ticks are rendered without overlapping.
   */
  letterSpacing?: number | string;
  /**
   * Controls how Recharts calculates "nice" tick values for this axis.
   * Options: `'none'`, `'auto'`, `'adaptive'`, `'snap125'`.
   * See {@link NiceTicksAlgorithm} for a full description of each option.
   *
   * @see {@link https://recharts.github.io/guide/axisTicks/}
   * @defaultValue 'auto'
   * @since 3.8
   */
  niceTicks?: NiceTicksAlgorithm;
}

export type Props<DataPointType = any, DataValueType = any> = Omit<
  PresentationAttributesAdaptChildEvent<TickItem, SVGTextElement>,
  'scale' | 'ref'
> &
  YAxisProps<DataPointType, DataValueType>;

function SetYAxisSettings(props: Omit<YAxisSettings, 'type'> & { type: AxisDomainTypeInput }): null {
  const dispatch = useAppDispatch();
  const prevSettingsRef = useRef<YAxisSettings | null>(null);
  const layout = useCartesianChartLayout();
  const { type: typeFromProps, ...restProps } = props;
  const evaluatedType: EvaluatedAxisDomainType | undefined = getAxisTypeBasedOnLayout(layout, 'yAxis', typeFromProps);

  const settings: YAxisSettings | undefined = useMemo(() => {
    if (evaluatedType == null) {
      return undefined;
    }
    return {
      ...restProps,
      type: evaluatedType,
    };
  }, [evaluatedType, restProps]);

  useLayoutEffect(() => {
    if (settings == null) {
      return;
    }
    if (prevSettingsRef.current === null) {
      dispatch(addYAxis(settings));
    } else if (prevSettingsRef.current !== settings) {
      dispatch(replaceYAxis({ prev: prevSettingsRef.current, next: settings }));
    }
    prevSettingsRef.current = settings;
  }, [settings, dispatch]);

  useLayoutEffect(() => {
    return () => {
      if (prevSettingsRef.current) {
        dispatch(removeYAxis(prevSettingsRef.current));
        prevSettingsRef.current = null;
      }
    };
  }, [dispatch]);

  return null;
}

function YAxisImpl(props: PropsWithDefaults) {
  const { yAxisId, className, width, label } = props;

  const cartesianAxisRef = useRef<CartesianAxisRef>(null);
  const labelRef = useRef(null);

  const viewBox = useAppSelector(selectAxisViewBox);
  const isPanorama = useIsPanorama();
  const dispatch = useAppDispatch();
  const axisType = 'yAxis';
  const axisSize: Size | undefined = useAppSelector(state => selectYAxisSize(state, yAxisId));
  const position = useAppSelector(state => selectYAxisPosition(state, yAxisId));
  const cartesianTickItems = useAppSelector(state => selectTicksOfAxis(state, axisType, yAxisId, isPanorama));
  /*
   * Here we select settings from the store and prefer to use them instead of the actual props
   * so that the chart is consistent. If we used the props directly, some components will use axis settings
   * from state and some from props and because there is a render step between these two, they might be showing different things.
   * https://github.com/recharts/recharts/issues/6257
   */
  const synchronizedSettings = useAppSelector(state => selectYAxisSettingsNoDefaults(state, yAxisId));

  useLayoutEffect(() => {
    // No dynamic width calculation is done when width !== 'auto'
    // or when a function/react element is used for label
    if (
      width !== 'auto' ||
      !axisSize ||
      isLabelContentAFunction(label) ||
      isValidElement(label) ||
      synchronizedSettings == null
    ) {
      return;
    }

    const axisComponent = cartesianAxisRef.current;
    if (!axisComponent) {
      return;
    }

    const updatedYAxisWidth = axisComponent.getCalculatedWidth();

    // if the width has changed, dispatch an action to update the width
    if (Math.round(axisSize.width) !== Math.round(updatedYAxisWidth)) {
      dispatch(updateYAxisWidth({ id: yAxisId, width: updatedYAxisWidth }));
    }
  }, [
    // The dependency on cartesianAxisRef.current is not needed because useLayoutEffect will run after every render.
    // The ref will be populated by then.
    // To re-run this effect when ticks change, we can depend on the ticks array from the store.
    cartesianTickItems,
    axisSize,
    dispatch,
    label,
    yAxisId,
    width,
    synchronizedSettings,
  ]);

  if (axisSize == null || position == null || synchronizedSettings == null) {
    return null;
  }

  const { dangerouslySetInnerHTML, ticks, scale: del, ...allOtherProps } = props;
  const { id, scale: del2, ...restSynchronizedSettings } = synchronizedSettings;

  return (
    <CartesianAxis
      {...allOtherProps}
      {...restSynchronizedSettings}
      ref={cartesianAxisRef}
      labelRef={labelRef}
      x={position.x}
      y={position.y}
      tickTextProps={width === 'auto' ? { width: undefined } : { width }}
      width={axisSize.width}
      height={axisSize.height}
      className={clsx(`recharts-${axisType} ${axisType}`, className)}
      viewBox={viewBox}
      ticks={cartesianTickItems}
      axisType={axisType}
      axisId={yAxisId}
    />
  );
}

export const yAxisDefaultProps = {
  allowDataOverflow: implicitYAxis.allowDataOverflow,
  allowDecimals: implicitYAxis.allowDecimals,
  allowDuplicatedCategory: implicitYAxis.allowDuplicatedCategory,
  angle: implicitYAxis.angle,
  axisLine: defaultCartesianAxisProps.axisLine,
  hide: false,
  includeHidden: implicitYAxis.includeHidden,
  interval: implicitYAxis.interval,
  label: false,
  minTickGap: implicitYAxis.minTickGap,
  mirror: implicitYAxis.mirror,
  orientation: implicitYAxis.orientation,
  padding: implicitYAxis.padding,
  reversed: implicitYAxis.reversed,
  scale: implicitYAxis.scale,
  tick: implicitYAxis.tick,
  tickCount: implicitYAxis.tickCount,
  tickLine: defaultCartesianAxisProps.tickLine,
  tickSize: defaultCartesianAxisProps.tickSize,
  type: implicitYAxis.type,
  niceTicks: implicitYAxis.niceTicks,
  width: implicitYAxis.width,
  yAxisId: 0,
} as const satisfies Partial<Props>;

type PropsWithDefaults<DataPointType = any, DataValueType = any> = RequiresDefaultProps<
  Props<DataPointType, DataValueType>,
  typeof yAxisDefaultProps
>;

const YAxisSettingsDispatcher = <DataPointType, DataValueType>(outsideProps: Props<DataPointType, DataValueType>) => {
  const props: PropsWithDefaults<DataPointType, DataValueType> = resolveDefaultProps(outsideProps, yAxisDefaultProps);
  return (
    <>
      <SetYAxisSettings
        interval={props.interval}
        id={props.yAxisId}
        scale={props.scale}
        type={props.type}
        domain={props.domain}
        allowDataOverflow={props.allowDataOverflow}
        dataKey={props.dataKey}
        allowDuplicatedCategory={props.allowDuplicatedCategory}
        allowDecimals={props.allowDecimals}
        tickCount={props.tickCount}
        padding={props.padding}
        includeHidden={props.includeHidden}
        reversed={props.reversed}
        ticks={props.ticks}
        width={props.width}
        orientation={props.orientation}
        mirror={props.mirror}
        hide={props.hide}
        unit={props.unit}
        name={props.name}
        angle={props.angle}
        minTickGap={props.minTickGap}
        tick={props.tick}
        tickFormatter={props.tickFormatter}
        niceTicks={props.niceTicks}
      />
      <YAxisImpl {...props} />
    </>
  );
};

/**
 * @consumes CartesianViewBoxContext
 * @provides CartesianLabelContext
 */
export const YAxis = React.memo(YAxisSettingsDispatcher, axisPropsAreEqual) as <
  DataPointType = any,
  DataValueType = any,
>(
  props: Props<DataPointType, DataValueType>,
) => ReactElement;
// @ts-expect-error we need to set the displayName for debugging purposes

YAxis.displayName = 'YAxis';
